// @ts-check
// Ao salvar opções removidas que ainda têm registros, pergunta para qual opção cada uma vai (optionMigrations).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Modal } from '../../../ui/modal.js';
import { Tag } from '../../../ui/chip.js';
import { Icon } from '../../../ui/icon.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { formatOptionUsage } from './select-options-editor.js';

const LEAVE_EMPTY = '';

/**
 * @typedef {{ value: string, label: string, color?: string }} OptionSummary
 */

/**
 * @param {{ removedOptions: OptionSummary[], targetOptions: OptionSummary[], usage: Record<string, number>, allowEmpty: boolean,
 *   excludedTargets?: ReadonlyArray<string>, usageLabel?: (count: number, value: string) => string, isSaving?: boolean,
 *   targetWarnings?: (fromValue: string, toValue: string) => string[],
 *   onCancel: () => void, onConfirm: (migrations: Record<string, string | null>) => void }} props
 *   targetWarnings: avisos do destino escolhido (etapas do funil: papéis que deixam de ver os cards movidos)
 */
export function OptionRemovalModal({
  removedOptions, targetOptions, usage, allowEmpty, excludedTargets = [], usageLabel, isSaving = false, targetWarnings, onCancel, onConfirm,
}) {
  const availableTargets = targetOptions.filter((option) => !excludedTargets.includes(option.value));
  const [targets, setTargets] = useState(/** @type {Record<string, string>} */ (
    Object.fromEntries(removedOptions.map((option) => [option.value, allowEmpty ? LEAVE_EMPTY : availableTargets[0]?.value ?? LEAVE_EMPTY]))
  ));
  const isComplete = allowEmpty || removedOptions.every((option) => targets[option.value]);
  return html`<${Modal}
    title=${t('dataModel.removal.title', { count: removedOptions.length })}
    subtitle=${t('dataModel.removal.subtitle')}
    confirmLabel=${t('dataModel.removal.confirm')}
    isConfirmDisabled=${!isComplete}
    isLoading=${isSaving}
    onClose=${onCancel}
    onConfirm=${() => onConfirm(Object.fromEntries(removedOptions.map((option) => [option.value, targets[option.value] || null])))}
  >
    <div class="data-model-removal-list">
      ${removedOptions.map((option) => {
        const count = usage[option.value] ?? 0;
        const selectId = `removal-target-${option.value}`;
        return html`<div key=${option.value} class="data-model-removal-row">
          <div class="data-model-removal-source">
            <${Tag} color=${option.color} text=${translateMetadataLabel(option.label)} />
            <span class="data-model-removal-count">${usageLabel ? usageLabel(count, option.value) : formatOptionUsage(count)}</span>
          </div>
          <label class="data-model-removal-target" for=${selectId}>
            <span class="form-field-label">${t('dataModel.removal.moveTo')}</span>
            <select id=${selectId} class="text-input data-model-select" value=${targets[option.value]}
              onChange=${(event) => setTargets({ ...targets, [option.value]: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
              ${allowEmpty ? html`<option value=${LEAVE_EMPTY}>${t('dataModel.removal.leaveEmpty')}</option>` : null}
              ${availableTargets.map((target) => html`<option key=${target.value} value=${target.value}>${translateMetadataLabel(target.label)}</option>`)}
            </select>
          </label>
          ${targetWarnings && targets[option.value]
            ? targetWarnings(option.value, targets[option.value]).map((warning) => html`<p key=${warning} class="data-model-removal-warning" role="note">
                <${Icon} name="IconAlertTriangle" size=${14} /><span>${warning}</span>
              </p>`)
            : null}
        </div>`;
      })}
    </div>
  <//>`;
}
